import { formatLabel } from "./format-text";

export interface HrmsPermissionNode {
  canView?:              boolean;   // r + :nop
  canViewById?:          boolean;   // r + :param (plain)
  canAdd?:               boolean;   // w
  canEdit?:              boolean;   // u / e + :param (plain)
  canDelete?:            boolean;   // d
  /**
   * Field names the current user is NOT allowed to see on this resource.
   * Populated from the `r + :param` entry's `attributes.not_allowed` array.
   * Falls back to the `r + :nop` entry when no :param entry exists (old format).
   */
  notAllowedAttributes?: string[];
  /**
   * Filter keys the current user is NOT allowed to apply on this resource.
   * Populated from the `r + :param` entry's `filters.not_allowed` array.
   * Falls back to the `r + :nop` entry when no :param entry exists (old format).
   */
  notAllowedFilters?:    string[];
  /**
   * Dynamic action map for compound operations that are NOT nested sub-resources.
   * Key = camelCase of the named segments extracted from the operation.
   *
   * Examples (standalone actions — no /:paramId sibling):
   *   ":param/status"                     → actions.status
   *   ":param/send"                       → actions.send
   *   ":param/documents/:param_id/verify" → actions.documentsVerify
   *   "public/welcome/:param/reject"      → sub-node "publicWelcome".actions.reject
   *
   * Sub-resources (operations that ALSO have a /:paramId variant) get their own
   * HrmsPermissionNode instead of a key in actions:
   *   ":param/response"       + ":param/response/:paramId" → response.canView / canAdd / canViewById / canEdit / canDelete
   */
  actions?: Record<string, boolean>;
  [subRoute: string]: HrmsPermissionNode | boolean | Record<string, boolean> | string[] | undefined;
}

export type HrmsPermissionTree = Record<string, HrmsPermissionNode>;

// ─── Constants ────────────────────────────────────────────────────────────────

// Only process entries belonging to this module
const ALLOWED_MODULES = ['hrms', 'inventory', 'sales', 'rental', 'manufacturing', 'purchase', 'accounts-and-finance', 'rbac', 'user', 'erp-common'] as const;
const ALWAYS_PREFIX_RESOURCE_KEYS = new Set([
  "delivery-orders",
  "settings",
]);
// Maps BE short keys → FE permission flags for plain CRUD operations.
// Only `:nop` and `:param` (no action suffix) are handled here.
// Compound operations (with named action segments) bypass this map and go to node.actions.
const HRMS_CRUD_FLAG_MAP: Record<string, Record<':nop' | ':param', keyof HrmsPermissionNode>> = {
  r: { ':nop': 'canView',   ':param': 'canViewById' },
  w: { ':nop': 'canAdd',    ':param': 'canAdd'      },
  u: { ':nop': 'canEdit',   ':param': 'canEdit'     },
  d: { ':nop': 'canDelete', ':param': 'canDelete'   },
  e: { ':nop': 'canEdit',   ':param': 'canEdit'     },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** "active-loans" → "activeloans",  "my-approvals" → "myapprovals" */
function toCamelCase(str: string): string {
  return str
    .replace(/[^a-zA-Z0-9\s-]/g, ' ')
    .split(/[-\s]+/)
    .filter(Boolean)
    .join('');
}

/**
 * Joins multiple camelCase parts into a single camelCase string.
 * ["documents", "verify"] → "documentsVerify"
 * ["public", "welcome"]   → "publicWelcome"
 */
function joinCamelParts(parts: string[]): string {
  return parts
    .map((p, i) => i === 0 ? p : p.charAt(0).toUpperCase() + p.slice(1))
    .join('');
}

/**
 * Checks whether a path segment is a parameter placeholder.
 * Matches ":param", ":id", ":param_id", ":nop", etc.
 */
function isParamSegment(seg: string): boolean {
  return seg.startsWith(':');
}

// ─── Operation Resolver ───────────────────────────────────────────────────────

interface ResolvedOperation {
  /**
   * camelCase key of the named sub-route (null = write to root node).
   * e.g. "active-loans" → "activeloans"
   *      "public/welcome" → "publicWelcome"
   */
  subRoute: string | null;

  /**
   * camelCase key of the specific action within the node (null = plain CRUD).
   * Populated when named segments appear *after* a param segment.
   * e.g. ":param/status"                    → "status"
   *      ":param/documents/:param_id/verify" → "documentsVerify"
   *      "public/welcome/:param/reject"      → "reject"  (sub-route = "publicWelcome")
   */
  action: string | null;

  /**
   * Whether a :param placeholder exists anywhere in the operation.
   * Determines canView vs canViewById for plain CRUD sub-route access.
   */
  hasParam: boolean;

  /**
   * Whether the operation ends with a param segment that follows trailing named segments.
   * true  → ":param/response/:paramId"  (sub-resource by-ID operation)
   * false → ":param/response"           (sub-resource list/create)
   * false → ":param/analyze"            (standalone action)
   * Used by the pre-scan to identify sub-resource action names.
   */
  hasTrailingParam: boolean;
}

/**
 * Generic operation parser — no hardcoded action names.
 *
 * Algorithm:
 *  1. Split operation by "/".
 *  2. Walk segments left-to-right, collecting:
 *       - "leading named" segments (before the first param)  → subRoute
 *       - "trailing named" segments (after the first param)  → action
 *  3. A pure ":nop" / ":param" operation has no named segments → plain CRUD.
 */
function resolveOperation(operation: string): ResolvedOperation {
  if (!operation || operation === ':nop') {
    return { subRoute: null, action: null, hasParam: false, hasTrailingParam: false };
  }

  const segments = operation.split('/');
  const leadingNamed: string[] = [];  // named segs before first param
  const trailingNamed: string[] = []; // named segs after first param
  let passedParam = false;
  let hasParam = false;

  for (const seg of segments) {
    if (isParamSegment(seg)) {
      passedParam = true;
      hasParam = true;
    } else {
      const part = toCamelCase(seg);
      if (part) {
        if (!passedParam) {
          leadingNamed.push(part);
        } else {
          trailingNamed.push(part);
        }
      }
    }
  }

  // true when the operation ends with a param that follows trailing named segs,
  // e.g. ":param/response/:paramId" — signals a sub-resource by-ID access.
  const lastSegment = segments[segments.length - 1];
  const hasTrailingParam = trailingNamed.length > 0 && isParamSegment(lastSegment);

  // Pure :param (no named segments at all) → plain CRUD
  if (leadingNamed.length === 0 && trailingNamed.length === 0) {
    return { subRoute: null, action: null, hasParam, hasTrailingParam: false };
  }

  const subRoute = leadingNamed.length > 0
    ? joinCamelParts(leadingNamed)
    : null;

  const action = trailingNamed.length > 0
    ? joinCamelParts(trailingNamed)
    : null;

  return { subRoute, action, hasParam, hasTrailingParam };
}

// ─── Tree Builder ─────────────────────────────────────────────────────────────

export function buildHrmsPermissionTree(raw: Record<string, any>): HrmsPermissionTree {
  const tree: HrmsPermissionTree = {};
  if(!raw){
    return tree;
  }
  for (const [rawModuleKey, crudBlock] of Object.entries(raw)) {

    // ── Step 1: normalise raw key → camelCase module name ──────────────────
    // "loan-allotments" → "loanAllotments"
    const formattedModule = formatLabel(
      rawModuleKey
        .split('/')[0]               // strip path suffix e.g. "/-p"
        .replace(/p$/gi, "")         // strip trailing p
        .replace(/-p-/gi, "-")       // strip -p- infix
        .replace(/-+$/g, "")         // clean leftover trailing dashes
    ).replace(/[^a-zA-Z0-9]/gi, ""); // strip non-alphanumeric

    // ── Step 2: split on capital letters ──────────────────────────────────
    // "loanAllotments"  → moduleKey: "loanAllotments",  moduleSubName: ""
    // "loanAllotmentsMy"→ moduleKey: "loanAllotments",  moduleSubName: "My"
    const parts         = formattedModule.split(/(?=[A-Z])/);
    const moduleKey     = parts.slice(0, 2).join("");
    const moduleSubName = parts.slice(2).join("");

    if (!crudBlock || typeof crudBlock !== 'object') continue;

    // ── Pre-scan: detect if multiple modules share this resource block ─────
    // When they do, each entry will be keyed as {Module}{ResourceKey} instead
    // of the plain {ResourceKey} so same-identifier entries don't collide.
    const modulesInBlock = new Set<string>();
    for (const [, blockEntries] of Object.entries(crudBlock)) {
      const blockList: any[] = Array.isArray(blockEntries) ? blockEntries : [blockEntries];
      for (const blockEntry of blockList) {
        const em = blockEntry?.identity?.module;
        if (em && ALLOWED_MODULES.includes(em as any)) modulesInBlock.add(em);
      }
    }
    const hasMultipleModules = modulesInBlock.size > 1;

    // ── Pre-scan: find action names that are sub-resources ────────────────
    // An action name is a sub-resource when at least one operation ends with
    // ":param/<name>/:paramId" — i.e. hasTrailingParam is true.
    // Those get their own HrmsPermissionNode with full CRUD flags instead of
    // a single boolean entry in the parent's `actions` map.
    const subResourceActions = new Set<string>();
    for (const [, subEntries] of Object.entries(crudBlock)) {
      const subList: any[] = Array.isArray(subEntries)
        ? subEntries
        : [{ ...(subEntries as any), identity: null }];
      for (const subEntry of subList) {
        const em = subEntry?.identity?.module;
        if (em && !ALLOWED_MODULES.includes(em)) continue;
        const op = subEntry?.identity?.operation ?? ':nop';
        const { action, hasTrailingParam } = resolveOperation(op);
        if (action && hasTrailingParam) subResourceActions.add(action);
      }
    }

    for (const [shortKey, entries] of Object.entries(crudBlock)) {
      const flagMap = HRMS_CRUD_FLAG_MAP[shortKey];
      if (!flagMap) continue;

      const list: any[] = Array.isArray(entries)
        ? entries
        : [{ ...entries, identity: null }];

      for (const entry of list) {
        // ── Filter: only process hrms module entries ────────────────────
        const entryModule = entry?.identity?.module;
        if (entryModule && !ALLOWED_MODULES.includes(entryModule)) continue;

        const operation = entry?.identity?.operation ?? ':nop';

        // ── Step 3: parse operation generically ────────────────────────
        const { subRoute, action, hasParam, hasTrailingParam } = resolveOperation(operation);
        const forcePrefix = ALWAYS_PREFIX_RESOURCE_KEYS.has(rawModuleKey.split('/')[0]);
        // ── Determine effective tree key ───────────────────────────────
        // When multiple backend modules share this resource block, prefix the
        // key with the module name so their entries don't collide.
        // e.g. "purchase" + "DeliveryOrders" → "PurchaseDeliveryOrders"
        const effectiveModuleKey = ((hasMultipleModules || forcePrefix) && entryModule)
          ? formatLabel(entryModule).replace(/[^a-zA-Z0-9]/gi, '') + moduleKey
          : moduleKey;

        if (!tree[effectiveModuleKey]) tree[effectiveModuleKey] = {};

        // ── Step 4: resolve target node ────────────────────────────────
        let targetNode: HrmsPermissionNode;
        const baseNode = tree[effectiveModuleKey];

        if (moduleSubName) {
          if (!(baseNode as any)[moduleSubName]) {
            (baseNode as any)[moduleSubName] = {} as HrmsPermissionNode;
          }
          const subNameNode = (baseNode as any)[moduleSubName] as any;

          if (subRoute) {
            const subKey = subRoute.charAt(0).toLowerCase() + subRoute.slice(1);
            if (!subNameNode[subKey]) subNameNode[subKey] = {} as HrmsPermissionNode;
            targetNode = subNameNode[subKey] as HrmsPermissionNode;
          } else {
            targetNode = subNameNode;
          }

        } else if (subRoute) {
          const subKey = subRoute.charAt(0).toLowerCase() + subRoute.slice(1);
          if (!(baseNode as any)[subKey]) {
            (baseNode as any)[subKey] = {} as HrmsPermissionNode;
          }
          targetNode = (baseNode as any)[subKey] as HrmsPermissionNode;

        } else {
          targetNode = baseNode;
        }

        // ── Step 5: write permission into target node ───────────────────
        if (action) {
          if (subResourceActions.has(action)) {
            // Sub-resource: create its own CRUD node rather than a boolean in actions.
            // ":param/response"          → response.canView / canAdd  (hasTrailingParam=false → ':nop')
            // ":param/response/:paramId" → response.canViewById / canEdit / canDelete  (':param')
            const subKey = action.charAt(0).toLowerCase() + action.slice(1);
            if (!(targetNode as any)[subKey]) {
              (targetNode as any)[subKey] = {} as HrmsPermissionNode;
            }
            const subNode = (targetNode as any)[subKey] as HrmsPermissionNode;

            const opKey: ':nop' | ':param' = hasTrailingParam ? ':param' : ':nop';
            const flag = flagMap[opKey];
            if (flag) subNode[flag] = true;

            if (shortKey === 'r') {
              if (hasTrailingParam) {
                // by-ID read — override with most-specific restrictions
                if (Array.isArray(entry?.attributes?.not_allowed)) {
                  subNode.notAllowedAttributes = entry.attributes.not_allowed;
                }
                if (Array.isArray(entry?.filters?.not_allowed)) {
                  subNode.notAllowedFilters = entry.filters.not_allowed;
                }
              } else {
                // list read — set only if not already set by a by-ID read
                if (Array.isArray(entry?.attributes?.not_allowed) && !subNode.notAllowedAttributes) {
                  subNode.notAllowedAttributes = entry.attributes.not_allowed;
                }
                if (Array.isArray(entry?.filters?.not_allowed) && !subNode.notAllowedFilters) {
                  subNode.notAllowedFilters = entry.filters.not_allowed;
                }
              }
            }

          } else {
            // Standalone action → generic boolean in the actions map.
            // e.g. ":param/status"                     → node.actions.status
            // e.g. ":param/documents/:param_id/verify" → node.actions.documentsVerify
            // e.g. "public/welcome/:param/reject"       → subNode.actions.reject
            if (!targetNode.actions) targetNode.actions = {};
            targetNode.actions[action] = true;
          }

        } else {
          // Plain CRUD operation → map to well-known flag
          const opKey: ':nop' | ':param' = hasParam ? ':param' : ':nop';
          const flag = flagMap[opKey];
          if (!flag) continue;
          targetNode[flag] = true;

          // Extract field-level restrictions from 'r' entries.
          // :param entries (canViewById) take priority over :nop (canView) because
          // detail views use the :param operation and have the most specific restrictions.
          if (shortKey === 'r') {
            const isParamRead = hasParam && !action;
            if (Array.isArray(entry?.attributes?.not_allowed)) {
              if (isParamRead || !targetNode.notAllowedAttributes) {
                targetNode.notAllowedAttributes = entry.attributes.not_allowed;
              }
            }
            if (Array.isArray(entry?.filters?.not_allowed)) {
              if (isParamRead || !targetNode.notAllowedFilters) {
                targetNode.notAllowedFilters = entry.filters.not_allowed;
              }
            }
          }
        }
      }
    }
  }

  return tree;
}
